import { EventObject } from '@xstate/store-react';
import { log } from '@domgen/shared/src/utils/log';
import { store } from '../store';
import { AppStatus } from '../../enums/appStatus';
import { StoreEvent } from '../../enums/storeEvent';
import { StoreContext } from '../../models/store/context';
import { clearSession } from '../../services/session/clear';
import { loadSession } from '../../services/session/load';

const SESSION_EXPIRY_MINUTES = 55;

/**
 * Initialises the store context on app load. This includes:
 * - Extracting URL parameters and storing in context
 * - Checking for existing session in sessionStorage and restoring if valid
 * - If no valid session, clearing any stale data and creating new session
 */
export const initialiseStore = <T extends EventObject = EventObject>(
  context: StoreContext,
  event:
    | (T & {
        data?: {
          journeyType?: 'test' | 'control';
          urlParams?: Record<string, string | undefined>;
          source?: string;
        };
      })
    | undefined,
  enqueue: { effect: (effect: () => Promise<void>) => void },
): StoreContext => {
  log.debug({ message: StoreEvent.INITIALISE, data: { context, event } });
  let hasUrlParams = false;
  if (event && event.data && event.data.urlParams) {
    log.debug({ message: 'Initialise event data', data: event.data });
    hasUrlParams = event.data.urlParams['mn'] || event.data.urlParams['sn'] ? true : false;
  }
  log.debug({ message: 'URL parameters present', data: { hasUrlParams } });

  enqueue.effect(async () => {
    // If entering with URL params (mn + sn), always start a new session and clear any existing session data to avoid conflicts
    log.debug({ message: 'URL parameters present', data: { hasUrlParams } });
    if (hasUrlParams) {
      clearSession();
      store.send({ type: StoreEvent.CREATE_SESSION });
      return;
    }

    const persisted = loadSession();

    if (persisted?.context.app.session?.dateCreated) {
      const sessionCreated = new Date(persisted.context.app.session.dateCreated).getTime();
      const ageMinutes = (Date.now() - sessionCreated) / 60000;

      if (ageMinutes < SESSION_EXPIRY_MINUTES) {
        const remainingMs = SESSION_EXPIRY_MINUTES * 60 * 1000 - (Date.now() - sessionCreated);
        store.send({
          type: StoreEvent.RESTORE_SESSION,
          data: {
            ...persisted.context,
            app: { ...persisted.context.app, status: AppStatus.READY },
            journeyType: persisted.context.journeyType ?? 'control',
          },
        });
        store.send({
          type: StoreEvent.START_SESSION_TIMER,
          data: { duration: remainingMs },
        });
      }
    }

    clearSession();
    store.send({ type: StoreEvent.CREATE_SESSION });
  });

  return {
    ...context,
    journeyType: event && event.data && event.data.journeyType ? event.data.journeyType : 'control',
    urlParams: event && event.data && event.data.urlParams ? event.data.urlParams : undefined,
    app: { ...context.app, status: AppStatus.LOADING },
  };
};
